<template>
  <NotificationSettingsFormSkeleton v-if="loading" />

  <div v-if="!loading">
    <div v-for="(setting, id) in notificationSettings" :key="id">
      <span class="text-gray-900 font-semibold">{{ getLabel(id) }}</span>
      <div class="flex items-center justify-between my-2 w-full">
        <label for="mail-switch">Per Mail benachrichtigen</label>
        <Switch id="mail-switch" v-model="setting.mail" />
      </div>
      <div class="flex items-center justify-between my-2 w-full">
        <label for="push-switch">Auf dem Handy benachrichtigen</label>
        <Switch id="push-switch" v-model="setting.push" />
      </div>
    </div>

    <DsAlert
      v-if="error"
      class="my-10"
      type="error"
      label="Es ist leider ein Fehler aufgetreten."
    >
      {{ error }}
    </DsAlert>

    <div class="text-right mt-5">
      <DsButton
        size="lg"
        :handler="save"
      >
        Speichern
      </DsButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  DsAlert,
  DsButton,
  NotificationVariant,
  useNotification
} from '@demvsystems/design-components';
import { getNotificationSettings, putNotificationSettings } from '@/api/account/accountApi';
import { onMounted, ref } from 'vue';
import { throwableCall } from '@/api/lib/integration';
import NotificationSettingsFormSkeleton from '@/user/components/NotificationSettingsFormSkeleton.vue';
import { captureException } from '@sentry/vue';
import type { NotificationSettings } from '@/api/account/types';
import Switch from "@/application/components/form/Switch.vue";
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';
const { show } = useNotification();
const { duzen } = useBaseDataStore();

const loading = ref<boolean>(true);
const error = ref<string>('');
const notificationSettings = ref<NotificationSettings | null>(null);

const labels = new Map<string, string>([
  ['handlungCreated', 'Neue Inhalte in der Inbox'],
]);

onMounted(async function () {
  try {
    loading.value = true;
    notificationSettings.value = await throwableCall(getNotificationSettings());
  } catch (err) {
    handleError(err);
  } finally {
    loading.value = false;
  }
});

async function save() {
  if (notificationSettings.value === null) {
    return;
  }

  try {
    await throwableCall(putNotificationSettings(notificationSettings.value));
    show('Die Benachrichtigungseinstellungen wurden erfolgreich gespeichert', NotificationVariant.Success);
    error.value = '';
  } catch (err) {
    handleError(err);
  }
}

function getLabel(id: string): string {
  return labels.get(id) ?? id;
}

function handleError(err) {
  captureException(err);
  error.value = `Es gab einen Fehler. Bitte ${duzen('lade', 'laden Sie')} die Seite neu oder
  ${duzen('probiere', 'probieren Sie')} es zu einem späteren Zeitpunkt nochmal.`
}
</script>
